import {
  useMutation,
  UseMutationResult,
  useQuery,
  UseQueryResult,
} from '@tanstack/react-query';
import {
  LegacyLogin_API_Response_Type,
  LegacyLogin_Query_Param_Type,
  Login_API_Response_Type,
  Login_Query_Param_Type,
  Logout_API_Response_Type,
  LegacyLogin_API_Payload_Data_Type,
  Login_API_Payload_Data_Type,
} from '../types/auth';
import { API_Error_Type } from '../axiosClient/axiosClient';
import useNotification from '../../hooks/useNotification';
import {
  getPartnerCompanies,
  legacyLogin,
  login,
  logout,
} from '../axiosNetworkRequest/auth';
import { useDispatch, useSelector } from 'react-redux';
import { ReduxActionDispatchType, ReduxRootStateType } from '../../redux/store';
import {
  RESET_USER_DETAILS,
  SET_USER_DETAILS,
  UPDATE_USER_PROFILE_DETAILS,
} from '../../redux/actionType/authAction';
import ls from 'localstorage-slim';
import { useNavigate } from 'react-router-dom';
import { queryClient } from '../reactQueryClient/reactQueryClient';
import { identify, track, resetAnalytics } from '../../utils/MixpanelHelper';
import { setSentryUser, clearSentryUser } from '../../utils/SentryHelper';

// General Description
// --- Purpose: To allow legacy login - preliminary login process
// --- Access: All users
export const useLegacyLogin = (): UseMutationResult<
  LegacyLogin_API_Response_Type,
  API_Error_Type,
  LegacyLogin_Query_Param_Type
> => {
  const { setMessage, setSeverity, handleSnackbarOpen } = useNotification();

  const navigate = useNavigate();

  return useMutation({
    mutationFn: (query_param: LegacyLogin_Query_Param_Type) => {
      query_param.setPreliminaryLoading(true);
      const api_endpoint: string = `auth/legacy-login`;
      const api_payload: LegacyLogin_API_Payload_Data_Type = {
        email_id: query_param.email_id,
        password: query_param.password,
        company_name: "The BPR",
      };
      return legacyLogin(api_endpoint, api_payload);
    },
    onSuccess: (api_res_data, query_param) => {
      // set the states for further processing
      query_param.setRefreshPassword(api_res_data.refresh_password);
      query_param.setMFA(api_res_data.mfa);
      query_param.setMFASkip(api_res_data.mfa_skip);
      query_param.setMFAQRCode(api_res_data.mfa_qr_code);
      query_param.setMFACooldownPeriod(api_res_data.mfa_cooldown_period);
      query_param.setMFASecret(api_res_data.mfa_device_secret);

      // check if refresh password step is applicable
      if (api_res_data.refresh_password) {
        // store the info in session storage
        sessionStorage.setItem('email_id', query_param.email_id);
        sessionStorage.setItem('refresh_token', api_res_data.refresh_token!);

        // navigate to refresh password page
        navigate('/refresh-password');
      }
      // if refresh password step is not applicable
      else {
        // MFA step is required
        if (api_res_data.mfa) {
          query_param.setLoginStep('mfa');
          query_param.setCentralEmailID(query_param.email_id);
          query_param.setCentralPassword(query_param.password);
          query_param.setPreliminaryLoading(false);

          // toast notification
          setMessage(api_res_data.msg);
          setSeverity('success');
          handleSnackbarOpen();
        }
        // MFA step is not required
        else {
          // call the next mutation function here
          query_param.mfa_login_mutation({
            email_id: query_param.email_id,
            password: query_param.password,
            mfa_skipped: false,
            mfa_code: '',
            setPreliminaryLoading: query_param.setPreliminaryLoading,
            login_type: 'legacy',
          });
        }
      }
    },
    onError: (error, query_param) => {
      query_param.setPreliminaryLoading(false);
      // toast notification
      setMessage(error.client_error_msg);
      setSeverity('error');
      handleSnackbarOpen();
    },
  });
};
// General Description
// --- Purpose: To allow login - final login process
// --- Access: All users
export const useLogin = (): UseMutationResult<
  Login_API_Response_Type,
  API_Error_Type,
  Login_Query_Param_Type
> => {
  const { setMessage, setSeverity, handleSnackbarOpen } = useNotification();

  // Redux dispatch hook
  const dispatch = useDispatch<ReduxActionDispatchType>();

  // React router hook
  const navigate = useNavigate();

  

  return useMutation({
    mutationFn: (query_param: Login_Query_Param_Type) => {
      const api_endpoint: string = `auth/login`;
      const api_payload: Login_API_Payload_Data_Type = {
        email_id: query_param.email_id,
        password: query_param.password,
        company_name: "The BPR",
        mfa_skipped: query_param.mfa_skipped,
        mfa_code: String(query_param.mfa_code),
        login_type: query_param.login_type,
    o_auth_id: "",
        o_auth_provider: "",
      };
      return login(api_endpoint, api_payload);
    },
    onSuccess: (api_res_data, query_param) => {
      // dispatch the action to reduxstore to store the response i.e., user details
      dispatch({ type: SET_USER_DETAILS, payload: api_res_data.user_data });
      // save the session id in local-storage to persist info upon reload/revisit
      localStorage.setItem('session_id', api_res_data.session_id);
      // save and encrypt user data in local-storage to persist info upon reload/revisit
      ls.set('AUI', api_res_data.user_data, { encrypt: true });
      // navigate to home-page
      navigate('/');

      identify(
        api_res_data.user_data.email_id,
        api_res_data.user_data.first_name,
        api_res_data.user_data.last_name,
        api_res_data.user_data.company_name,
      );
      setSentryUser(
        api_res_data.user_data.email_id,
        `${api_res_data.user_data.first_name} ${api_res_data.user_data.last_name}`,
        api_res_data.user_data.company_name,
      );
      track('Login User', {
        'Name':         `${api_res_data.user_data.first_name} ${api_res_data.user_data.last_name}`,
        'Email':        api_res_data.user_data.email_id,
        'Company Name': api_res_data.user_data.company_name,
        'Role':         api_res_data.user_data.mh_admin ? 'Admin' : 'Standard',
        'Login Type':   query_param.login_type,
      });

      query_param.setPreliminaryLoading(false);

      // toast notification
      setMessage(api_res_data.msg);
      setSeverity('success');
      handleSnackbarOpen(true);
    },
    onError: (error, query_param) => {
      // update the preliminary login state
      query_param.setPreliminaryLoading(false);

      // toast notification
      setMessage(error.client_error_msg);
      setSeverity('error');
      handleSnackbarOpen();
    },
  });
};

// General Description
// --- Purpose: To logout
// --- Access: All users
export const useLogout = (option?: {
  enabled: boolean;
}): UseQueryResult<Logout_API_Response_Type, API_Error_Type> => {
  const api_endpoint: string = `auth/logout`;
  const query_key: [string] = ['logout'];

  const { cleanup } = useLogoutCleanup();

  return useQuery<Logout_API_Response_Type, API_Error_Type>({
    queryKey: query_key,
    queryFn: () => logout(api_endpoint),
    select: cleanup,
    enabled: option?.enabled ?? true,
    gcTime: 0,
    staleTime: 0,
    refetchOnMount: false,
  });
};

// helper function to perform logout cleanup
export const useLogoutCleanup = () => {
  const { setMessage, setSeverity, handleSnackbarOpen } = useNotification();

  // Redux dispatch hook
  const dispatch = useDispatch<ReduxActionDispatchType>();

  const userInfo = useSelector((state: ReduxRootStateType) => state.user.user_info);

  function cleanup(api_res: Logout_API_Response_Type) {
    track('Logout User', {
      'Name':         `${userInfo?.first_name} ${userInfo?.last_name}`,
      'Email':        userInfo?.email_id,
      'Company Name': userInfo?.company_name,
    });
    resetAnalytics();
    clearSentryUser();

    // cleanup the local storage
    localStorage.removeItem('session_id');
    localStorage.removeItem('AUI');

    // cleanup the redux store
    dispatch({ type: RESET_USER_DETAILS });

    // clear all cached data
    queryClient.getQueryCache().clear();

    setMessage(api_res.msg);
    setSeverity(api_res.msg === 'Session expired' ? 'error' : 'success');
    handleSnackbarOpen();

    return api_res;
  }

  return {
    cleanup,
  };
};

export const useGetPartnerCompanies = () => {
  const dispatch = useDispatch<ReduxActionDispatchType>();

  return useQuery({
    queryKey: ['partner-companies'],
    queryFn: async () => {
      const res = await getPartnerCompanies();
      dispatch({ type: UPDATE_USER_PROFILE_DETAILS, payload: { partner_company: res.data } });
      const existing = ls.get<Record<string, unknown>>('AUI', { decrypt: true }) ?? {};
      ls.set('AUI', { ...existing, partner_company: res.data }, { encrypt: true });
      return res;
    },
    staleTime: 5 * 60 * 1000,
  });
};
